/**
 * Filters for specialties.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { SPECIALTY_STATUS_CONFIG } from './specialty-configs';
import type { SpecialtyFilters } from './specialty-configs';

/** Props for {@link SpecialtyFiltersContent}. */
interface SpecialtyFiltersContentProps {
  filters: SpecialtyFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onSetSearchTerm: (term: string) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Toggle chips for specialty status. */
const StatusFilter: React.FC<{
  filters: SpecialtyFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {Object.entries(SPECIALTY_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.statuses.includes(key);
        const color = config.badgeColor || 'rgb(var(--status-success))';
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': color } as React.CSSProperties}
            onClick={() => onToggleStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Free-text search across specialty code / label. */
const SearchFilter: React.FC<{
  filters: SpecialtyFilters;
  onSetSearchTerm: (term: string) => void;
}> = ({ filters, onSetSearchTerm }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-64">
      <SearchInput
        size="sm"
        shortcut={false}
        placeholder="Code ou libellé..."
        value={filters.searchTerm ?? ''}
        onChange={(e) => onSetSearchTerm(e.target.value)}
        onClear={() => onSetSearchTerm('')}
      />
    </div>
  </div>
);

/** Ribbon of specialty filters; each group is gated by RBAC + configure-panel visibility. */
export const SpecialtyFiltersContent: React.FC<SpecialtyFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onSetSearchTerm,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}
      {show('filter-search') && (
        <SearchFilter filters={filters} onSetSearchTerm={onSetSearchTerm} />
      )}
    </div>
  );
};
